import cc from "classcat"
import * as dateFns from "date-fns"
import React, { Suspense } from "react"
import { graphql, useLazyLoadQuery } from "react-relay"

import styles from "./IntegrationSyncHistory.module.css"

import { IntegrationSyncHistoryQuery } from "./__generated__/IntegrationSyncHistoryQuery.graphql"

import { ModalBody, ModalFormWrapper } from "~/common/ModalForm"
import { TangramPersonRunner } from "~/common/tangrams"

import { IntegrationSyncNowButton } from "./IntegrationSyncNowButton"

const safeJSONStringify = (input: string): string => {
  if (input[0] !== "{") {
    return input
  }
  try {
    return JSON.stringify(JSON.parse(input), null, 2)
  } catch {
    return input
  }
}

type Message = {
  type: "INFO" | "ERROR"
  timestamp: string
  service: string
  text: string
}

const messageLineRegExp = /^(\S+) (\w+) (INFO|ERROR) (.+)$/

const parseMessages = (rawMessages: readonly string[]): Message[][] => {
  const messages: Message[] = rawMessages.map((line): Message => {
    const match = line.match(messageLineRegExp)
    if (!match) {
      throw new Error("Could not parse line.")
    }

    return {
      timestamp: match[1],
      service: match[2],
      type: match[3] as Message["type"],
      text: safeJSONStringify(match[4].trim()),
    }
  })

  return messages
    .sort((a, b) => {
      return a.timestamp.localeCompare(b.timestamp)
    })
    .reduce((groups, message) => {
      const lastGroup = groups[groups.length - 1]
      if (!lastGroup) {
        groups.push([message])
        return groups
      }

      const lastMessage = lastGroup[lastGroup.length - 1]
      if (!lastMessage || lastMessage.service === message.service) {
        lastGroup.push(message)
        return groups
      }

      groups.push([message])
      return groups
    }, [])
}

type IntegrationSync = {
  info_messages: readonly string[]
  started_at: string
  finished_at: string | null
}

type LogCellProps = {
  sync: IntegrationSync
}

const InfoErrorLogs = (props: LogCellProps) => {
  const { sync } = props
  const { info_messages: rawMessages } = sync

  const messages = parseMessages(rawMessages)

  const formattedMessages = messages.map((group, groupIndex) => {
    const firstMessage = group[0]
    return (
      <div key={groupIndex}>
        <h2>{firstMessage.service}</h2>
        {group.map((message, messageIndex) => {
          const date = dateFns.format(
            dateFns.parseISO(message.timestamp),
            "HH:mm:ss",
          )
          return (
            <span key={messageIndex}>
              <span className={styles.messageTimestamp}>{date}</span>{" "}
              <span
                className={cc({
                  [styles.infoMessage]: message.type === "INFO",
                  [styles.errorMessage]: message.type === "ERROR",
                })}
              >
                {message.text}
              </span>
              {"\n"}
            </span>
          )
        })}
      </div>
    )
  })

  return (
    <pre className={styles.logContainer}>
      <code>{formattedMessages}</code>
    </pre>
  )
}

type DurationCellProps = {
  sync: IntegrationSync | undefined
}

const Metadata = (props: DurationCellProps) => {
  const { sync } = props

  if (!sync) {
    return <div className={styles.metadata}>No record of last sync.</div>
  }

  const { started_at: startedAtDateString, finished_at: finishedAtDateString } =
    sync

  const startedAt = dateFns.parseISO(startedAtDateString)
  const finishedAt = finishedAtDateString
    ? dateFns.parseISO(finishedAtDateString)
    : undefined

  const durationMs = finishedAt
    ? dateFns.differenceInMilliseconds(finishedAt, startedAt)
    : undefined

  return (
    <div className={styles.metadata}>
      Started syncing at {dateFns.format(startedAt, "PP pp")}.{" "}
      {finishedAt && (
        <>Completed in {Math.round(durationMs / 1000)} second(s).</>
      )}
    </div>
  )
}

type LazyIntegrationSyncHistoryProps = {
  integration: {
    id: number
    active: boolean
  }
}

export const LazyIntegrationSyncHistory = (
  props: LazyIntegrationSyncHistoryProps,
) => {
  const { integration } = props

  const query = useLazyLoadQuery<IntegrationSyncHistoryQuery>(
    graphql`
      query IntegrationSyncHistoryQuery($integrationId: Int!) {
        integration_by_pk(id: $integrationId) {
          id
          integration_syncs(order_by: { id: desc }, limit: 1) {
            id
            state
            started_at
            finished_at
            info_messages
          }
        }
      }
    `,
    {
      integrationId: integration.id,
    },
  )

  const lastSync = query.integration_by_pk.integration_syncs[0]

  return (
    <>
      <div className={styles.header}>
        <Metadata sync={lastSync} />
        <IntegrationSyncNowButton
          integration={integration}
          lastIntegrationSync={lastSync}
        />
      </div>
      {lastSync && <InfoErrorLogs sync={lastSync} />}
    </>
  )
}

type IntegrationSyncHistoryProps = LazyIntegrationSyncHistoryProps & {
  onClose: () => void
}

const IntegrationSyncHistory = (props: IntegrationSyncHistoryProps) => {
  const { integration, onClose } = props

  return (
    <ModalFormWrapper
      tangram={<TangramPersonRunner />}
      tangramStyles={{ bottom: 20 }}
      headerTitle={"Details of Last Sync"}
      wide
      onClose={onClose}
    >
      <ModalBody wide className={styles.modalBody}>
        <Suspense fallback="Loading…">
          <LazyIntegrationSyncHistory integration={integration} />
        </Suspense>
      </ModalBody>
    </ModalFormWrapper>
  )
}

export { IntegrationSyncHistory }
